import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import Layout from "./layout/Layout";
import Main from "./layout/Main";
import PublicRoutes from "./layout/PublicRoutes";
import SignUpPage from "./pages/Login/SignUp";
import SignInPage from "./pages/Login/SignIn";
import OnboardingSteps from "./pages/Login/OnboardingSteps";
import QuestDetails from "./pages/Quest/Details/QuestDetails";
import Quest from "./pages/Quest/Quest";
import Profile from "./pages/Profile/Profile";
import EditUser from "./pages/Profile/EditUser/EditUser";
import TaskResults from "./pages/Quest/QuestResult/QuestResult";
import TakeQuestResult from "./pages/Quest/QuestResult/TakeQuestResult";
import PrivateRoutes from "./layout/PrivateRoutes";
import Performance from "./pages/Performan/Performance";
import ForgotPasswordPage from "./pages/Login/ForgotPassword";
import EmailOTPPage from "./pages/Login/VerifyOTP";
import ResetPasswordPage from "./pages/Login/ResetPassword";
import SearchCompanyPage from "./pages/CompanyProfile/SearchCompany";
import CompanyProfilePage from "./pages/CompanyProfile/CompanyProfile";
import PerformaceAgent from "./pages/Performan/Agent/PerformaceAgent";
import OnGoing from "./pages/Home/OnGoing";
import QuestOndemand from "./pages/Quest/Ondemand/Ondemand";
import PerformanceChat from "./pages/Performan/Chat/PerformanceChat";
import ManualInput from "./pages/Quest/Ondemand/ManualInput";
import TopUp from "./pages/Profile/TopUp/TopUp";
import HistoryTopUp from "./pages/Profile/HistoryTopUp/HistoryTopUp";
import OndemandChat from "./pages/Quest/Ondemand/ChatOndemand";
import Question from "./pages/Quest/QuestionAnswer/Question";
import QuestionQualify from "./pages/Quest/StepOnDemand/QuestionComponent/QualifyQuestion";
import QRCode from "./pages/QRCode/QRCode";
import NewHome from "./pages/Home/NewHome";

// Page titles mapping
const pageTitles = {
  "/": "Home",
  "/profile": "Profile",
  "/profile/edit": "Edit Profile",
  "/profile/withdraw": "Withdraw",
  "/profile/history": "Withdrawal History",
  "/profile/rewards": "Rewards",
  "/profile/rewards/detail/:id": "Reward Detail",
  "/profile/achievements": "Achievements",
  "/profile/achievements/detail/:id": "Achievement Detail",
  "/quest": "Quest",
  "/quest/details/multiple/choice/:id": "Take Quest",
  "/quest/details/completed": "Quest Completion",
  "/register": "Sign Up",
  "/sign-in": "Sign In",
  "/verify-otp": "Verify OTP",
  "/qr-scan": "QR Scan",
  "/preview": "Onboarding Steps",
  "/quest/details/:id": "Quest Details",
  "/ongoing": "Ongoing",
  "/ondemand": "Quest Ondemand",
  "/ondemand/ondemand-chat": "Ondemand Chat",
  "/ondemand/question": "Question",
  "/ondemand/add-objective": "Manual Input",
  "/quest/result/:id": "Task Results",
  "/quest/result/take/:id": "Take Quest Result",
  "/performan": "Performance",
  "/performan/agent": "Performance Agent",
  "/performan/chat": "Performance Chat",
  "/qr-code": "QR Code",
  "/search-company": "Search Company",
  "/profile-company": "Company Profile",
  "/forgot-password": "Forgot Password",
  "/reset-password": "Reset Password",
};

// Utility function to hash PII (SHA-256)

// Utility function to get page title from route
const getPageTitle = (pathname, pageTitles) => {
  if (pageTitles[pathname]) {
    return pageTitles[pathname];
  }
  for (const route in pageTitles) {
    const regex = new RegExp(`^${route.replace(/:[^\s/]+/g, "[^/]+")}$`);
    if (regex.test(pathname)) {
      return pageTitles[route];
    }
  }
  return "Unknown Page";
};

// Safe gtag wrapper
const safeGtag = (...args) => {
  if (window.gtag && typeof window.gtag === "function") {
    window.gtag(...args);
  } else {
    console.warn("gtag is not available, skipping:", args);
  }
};

function App() {
  const location = useLocation();
  const [userInfo, setUserInfo] = useState(null);
  const [gtagLoaded, setGtagLoaded] = useState(false);
  const [gtagBlocked, setGtagBlocked] = useState(false);

  const measurementId = process.env.REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID;

  // Validate Measurement ID
  useEffect(() => {
    if (!measurementId) {
      console.error(
        "REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID is not defined in .env"
      );
      setGtagBlocked(true);
    }
  }, [measurementId]);

  // Load userInfo from localStorage on mount
  useEffect(() => {
    try {
      const storedUserInfo = localStorage.getItem("userInfo");
      if (storedUserInfo) {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        if (
          parsedUserInfo &&
          parsedUserInfo.id &&
          parsedUserInfo.company_name
        ) {
          setUserInfo(parsedUserInfo);
        } else {
          localStorage.removeItem("userInfo");
          setUserInfo(null);
        }
      }
    } catch (error) {
      localStorage.removeItem("userInfo");
      setUserInfo(null);
    }
  }, []);

  // Load gtag.js script
  useEffect(() => {
    if (!measurementId || gtagBlocked) return;

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.onload = () => {
      setGtagLoaded(true);
    };
    script.onerror = () => {
      console.error(
        "Failed to load gtag.js, possibly blocked by ad blocker or network"
      );
      setGtagBlocked(true);
    };
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [measurementId, gtagBlocked]);

  // Poll for gtag availability
  useEffect(() => {
    if (!gtagLoaded || gtagBlocked || !measurementId) return;

    const maxAttempts = 20;
    let attempts = 0;

    const checkGtag = setInterval(() => {
      if (window.gtag && typeof window.gtag === "function") {
        clearInterval(checkGtag);

        safeGtag("js", new Date());
        safeGtag("config", measurementId);

        if (userInfo) {
          try {
            const { id, company_name, phone, email } = userInfo;

            safeGtag("set", "user_properties", {
              custom_user_id: id,
              company_name: company_name || "unknown",
              hashed_phone: phone || "unknown",
              hashed_email: email || "unknown",
              view_frequency: 0,
            });

            safeGtag("config", measurementId, {
              custom_user_id: id,
            });
          } catch (error) {
            console.error("Error processing userInfo for GA4:", error);
          }
        } else {
          safeGtag("set", "user_properties", {
            custom_user_id: null,
            company_name: null,
            hashed_phone: null,
            hashed_email: null,
            view_frequency: 0,
          });
          safeGtag("config", measurementId, { custom_user_id: null });
        }
      } else {
        attempts++;
        console.log(`gtag not available, attempt ${attempts}/${maxAttempts}`);
        if (attempts >= maxAttempts) {
          setGtagBlocked(true);
          clearInterval(checkGtag);
        }
      }
    }, 500);

    return () => clearInterval(checkGtag);
  }, [gtagLoaded, gtagBlocked, userInfo, measurementId]);

  // Track page views and view frequency
  useEffect(() => {
    if (gtagLoaded && !gtagBlocked && userInfo?.id && measurementId) {
      const pageTitle = getPageTitle(location.pathname, pageTitles); // Fixed argument
      safeGtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_title: pageTitle,
      });

      safeGtag("event", "view_frequency", {
        custom_user_id: userInfo.id,
        value: 1,
      });
    }
  }, [gtagLoaded, gtagBlocked, location, userInfo, measurementId]);

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route element={<Main />}>
          <Route element={<PrivateRoutes />}>
            <Route path="/" element={<Outlet />}>
              <Route index element={<NewHome />} />
              <Route path="ongoing" element={<OnGoing />} />
            </Route>
            <Route path="/profile" element={<Outlet />}>
              <Route index element={<Profile />} />
              <Route path="edit" element={<EditUser />} />
              <Route path="topup" element={<TopUp />} />
              <Route path="historytopup" element={<HistoryTopUp />} />
            </Route>
            <Route path="/ondemand" element={<Outlet />}>
              <Route index element={<QuestOndemand />} />
              <Route path="ondemand-chat" element={<OndemandChat />} />
              <Route path="question" element={<Question />} />
              <Route path="add-objective" element={<ManualInput />} />
            </Route>
            <Route path="/quest" element={<Outlet />}>
              <Route index element={<Quest />} />
              <Route path="details/:id" element={<QuestDetails />} />
              <Route path="qualify" element={<QuestionQualify />} />
              <Route path="result/:id" element={<TaskResults />} />
              <Route path="result/take/:id" element={<TakeQuestResult />} />
            </Route>
            <Route path="/performan" element={<Outlet />}>
              <Route index element={<Performance />} />
              <Route path="agent" element={<PerformaceAgent />} />
              <Route path="chat" element={<PerformanceChat />} />
            </Route>
            <Route path="/qr-code" element={<Outlet />}>
              <Route index element={<QRCode />} />
            </Route>
            <Route path="/search-company" element={<SearchCompanyPage />} />
            <Route path="/profile-company" element={<CompanyProfilePage />} />
          </Route>
          <Route element={<PublicRoutes />}>
            <Route path="/register" element={<SignUpPage />} />
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/verify-otp" element={<EmailOTPPage />} />
            <Route path="/preview" element={<OnboardingSteps />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="*" element={<Navigate to="/sign-in" />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}

export default App;
